import cookie from 'js-cookie';
import { IncomingMessage } from 'http';

import {
  COOKIES,
  CHOOSE_ENDPOINT,
  DY_CLIENT_SIDE_API_BASE_URL,
  DY_SERVER_SIDE_API_BASE_URL,
  ENGAGEMENTS_ENDPOINT,
  EVENTS_ENDPOINT,
  DY_CLIENT_SIDE_API_COLLECT_BASE_URL,
  PAGEVIEW_ENDPOINT,
} from './constants';

import {
  ChooseRequestBody,
  EventsRequestBody,
  EngagementsRequestBody,
  ChooseRequestResponse,
  DyRecommendationRule,
  DyRecommendationSlot,
  ChooseRequestContext,
  PageViewRequestBody,
} from './types';

interface DYFetchParams {
  url: string;
  body: ChooseRequestBody | EventsRequestBody | EngagementsRequestBody | PageViewRequestBody;
  path?: string;
}

function createDyFetch(apiKey: string, req?: IncomingMessage) {
  let userAgent = '';
  let dyid = '';
  let dyid_server = '';
  let dy = '';
  let location = '';
  // We only report IP for server-side requests.
  // For client side, DY will determine it automatically
  let ip: string | null = null;

  if (req) {
    dyid = req.cookies[COOKIES.dyId];
    dyid_server = req.cookies[COOKIES.dyServerId];
    dy = req.cookies[COOKIES.dySession];
    userAgent = req.headers['user-agent'];
    location = req.url;
    ip =
      (req.headers['x-forwarded-for']
        ? // On prod/staging this is set by AWS stack
          `${req.headers['x-forwarded-for']}`.split(',')[0]
        : // Local dev
          req.socket?.remoteAddress) || null;
  }

  return async ({ url, body, path }: DYFetchParams) => {
    if (!req && typeof window !== 'undefined') {
      dyid = cookie.get(COOKIES.dyId);
      dyid_server = cookie.get(COOKIES.dyServerId);
      dy = cookie.get(COOKIES.dySession);
      userAgent = window.navigator.userAgent;
      location = window.location.href;
    }

    location = path || location;

    const { context, ...rest } = body;
    const page = context?.page || {};

    return await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'dy-api-key': apiKey,
      },
      body: JSON.stringify({
        user: {
          dyid,
          dyid_server,
        },
        session: { dy },
        context: {
          ...context,
          page: { ...page, location, referrer: location },
          device: ip ? { userAgent, ip } : { userAgent },
        },
        ...rest,
      }),
    });
  };
}

interface CreateClientParams {
  serverSideApiKey: string;
  clientSideApiKey: string;
}

interface ChooseParams {
  body: ChooseRequestBody;
  req?: IncomingMessage;
}

interface RecommendationParams {
  campaignName: string;
  recommendationRules: DyRecommendationRule[];
  context: ChooseRequestContext;
  req?: IncomingMessage;
}

export interface RecommendationsResponse {
  title: string;
  recommendations: DyRecommendationSlot[];
  decisionId: string;
}

export interface DyClient {
  // TODO: remove path after getServerSideProps migration
  choose: (params: ChooseParams, path?: string) => Promise<ChooseRequestResponse>;
  events: (body: EventsRequestBody) => Promise<Response>;
  engagements: (body: EngagementsRequestBody) => Promise<Response>;
  recommendations: (params: RecommendationParams) => Promise<RecommendationsResponse>;
  pageView: (productId: string, body: PageViewRequestBody) => Promise<Response>;
  isEnabled: boolean;
}

export function createClient(params: CreateClientParams): DyClient {
  const { clientSideApiKey, serverSideApiKey } = params;
  const isEnabled = !!clientSideApiKey && !!serverSideApiKey;

  if (!isEnabled) {
    return {
      choose: () => Promise.resolve({} as any),
      recommendations: () => Promise.resolve({ recommendations: [] } as any),
      events: () => Promise.resolve({} as any),
      engagements: () => Promise.resolve({} as any),
      pageView: () => Promise.resolve({} as any),
      isEnabled,
    };
  }

  // Choose api should work on both client and server side
  const choose: DyClient['choose'] = async (params: ChooseParams, path?: string) => {
    const { body, req } = params;
    const apiKey = req ? serverSideApiKey : clientSideApiKey;
    const baseUrl = req ? DY_SERVER_SIDE_API_BASE_URL : DY_CLIENT_SIDE_API_BASE_URL;
    const dyFetch = createDyFetch(apiKey, req);
    try {
      const res = await dyFetch({
        url: [baseUrl, CHOOSE_ENDPOINT].join(''),
        body,
        path,
      });
      return await res.json();
    } catch (err) {
      console.log(err);
    }
  };

  const recommendations: DyClient['recommendations'] = async (params: RecommendationParams) => {
    const { req, campaignName, recommendationRules, context } = params;

    // If we pass category-page data here dy will use this as a filter. But DY
    // is not capable of assigning products to multiple categories. Therefore
    // discovery sends a feed with every product only being assigned to a single
    // category For example the category 'Couchgarnituren' in
    // '/wohnen/sofas/couchgarnituren' is not defined in the feed. Products in
    // this category are assigned to 'Polstermöbel'. So we cannot rely on the
    // category shown in the frontend also being the category the data is
    // assigned to. Therefore we leave the category value out and match by
    // 'keyword' via realtimeRules
    const updatedContext = {
      ...context,
      page: {
        ...context.page,
        data: ['CART', 'PRODUCT'].includes(context?.page?.type) ? context?.page?.data || [] : [],
      },
    };
    const response = await choose({
      req,
      body: {
        context: updatedContext,
        selector: {
          names: [campaignName],
          args: {
            [campaignName]: {
              realtimeRules: recommendationRules,
            },
          },
        },
      },
    });

    const choices = response?.choices || [];
    const variations = choices.map((choice) => choice.variations).flat() || [];
    const decisionId = choices.find((choice) => choice.name === campaignName)?.decisionId || '';
    const recommendations = variations.map((variation) => variation.payload?.data?.slots || []).flat();
    const title = variations?.[0]?.payload?.data?.custom?.title || '';

    return { title, recommendations, decisionId };
  };

  const events = (body: EventsRequestBody) => {
    const dyFetch = createDyFetch(clientSideApiKey);
    return dyFetch({
      url: [DY_CLIENT_SIDE_API_COLLECT_BASE_URL, EVENTS_ENDPOINT].join(''),
      body,
    });
  };

  const engagements = (body: EngagementsRequestBody) => {
    const dyFetch = createDyFetch(clientSideApiKey);
    return dyFetch({
      url: [DY_CLIENT_SIDE_API_COLLECT_BASE_URL, ENGAGEMENTS_ENDPOINT].join(''),
      body,
    });
  };

  const pageView = (productId: string, { context }: PageViewRequestBody) => {
    const dyFetch = createDyFetch(clientSideApiKey);
    return dyFetch({
      url: [DY_CLIENT_SIDE_API_COLLECT_BASE_URL, PAGEVIEW_ENDPOINT].join(''),
      body: {
        context: {
          page: { ...context.page, type: 'PRODUCT', data: [productId] },
        },
      },
    });
  };

  return {
    choose,
    events,
    engagements,
    recommendations,
    pageView,
    isEnabled,
  };
}
